import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ChartContainer } from "@/components/ui/chart";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell
} from "recharts";
import { Plus, Trash2, Edit, BarChart3, LineChart as LineChartIcon, PieChart as PieChartIcon, TrendingUp } from "lucide-react";
import { DynamicChart } from "./DynamicChart";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Widget {
  id: string;
  type: 'kpi' | 'chart';
  title: string;
  chartType?: 'bar' | 'line' | 'pie' | 'area';
  data: any[];
  color?: string;
  size: 'small' | 'medium' | 'large';
  position: { x: number; y: number };
}

const WIDGET_TEMPLATES = [
  {
    type: 'kpi',
    title: 'Total Revenue',
    data: [{ value: 125000, format: 'currency', trend: 'up', change: 12.5 }],
    size: 'small'
  },
  {
    type: 'chart',
    title: 'Monthly Sales',
    chartType: 'line',
    data: [
      { name: "Jan", value: 4000 },
      { name: "Feb", value: 3000 },
      { name: "Mar", value: 6000 },
      { name: "Apr", value: 8000 },
      { name: "May", value: 7000 },
      { name: "Jun", value: 9000 }
    ],
    size: 'medium'
  },
  {
    type: 'chart',
    title: 'Category Distribution',
    chartType: 'pie',
    data: [
      { name: "Product Sales", value: 45 },
      { name: "Services", value: 30 },
      { name: "Consulting", value: 25 }
    ],
    size: 'medium'
  }
];

export const CustomizableWidgets = () => {
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [isAddingWidget, setIsAddingWidget] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [dataSources, setDataSources] = useState<any[]>([]);
  const [widgetTitle, setWidgetTitle] = useState('');

  useEffect(() => {
    loadDataSources();
    loadDefaultWidgets();
  }, []);

  const loadDataSources = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('file_storage')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_folder', false)
        .limit(10);

      if (error) throw error;
      setDataSources(data || []);
    } catch (error) {
      console.error('Error loading data sources:', error);
    }
  };

  const loadDefaultWidgets = () => {
    const defaultWidgets: Widget[] = WIDGET_TEMPLATES.map((template, index) => ({
      id: `widget-${index}`,
      type: template.type as 'kpi' | 'chart',
      title: template.title,
      chartType: template.chartType as any,
      data: template.data,
      size: template.size as 'small' | 'medium' | 'large',
      position: { x: 0, y: index * 2 },
      color: `hsl(${index * 120}, 70%, 50%)`
    }));
    setWidgets(defaultWidgets);
  };

  const handleAddWidget = () => {
    if (!selectedTemplate || !widgetTitle) {
      toast.error('Please select a template and enter a title');
      return;
    }

    const newWidget: Widget = {
      id: `widget-${Date.now()}`,
      type: selectedTemplate.type,
      title: widgetTitle,
      chartType: selectedTemplate.chartType,
      data: selectedTemplate.data,
      size: selectedTemplate.size,
      position: { x: 0, y: widgets.length * 2 },
      color: `hsl(${widgets.length * 60}, 70%, 50%)`
    };

    setWidgets(prev => [...prev, newWidget]);
    setIsAddingWidget(false);
    setWidgetTitle('');
    setSelectedTemplate(null);
    toast.success("New widget added to dashboard");
  };

  const removeWidget = (widgetId: string) => {
    setWidgets(prev => prev.filter(w => w.id !== widgetId));
    toast.success("Widget removed");
  };

  const renderKPIWidget = (widget: Widget) => {
    const kpiData = widget.data[0];
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-muted-foreground">{widget.title}</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => removeWidget(widget.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-3xl font-bold">
            {kpiData.format === 'currency' ? '$' : ''}
            {kpiData.value.toLocaleString()}
            {kpiData.format === 'percentage' ? '%' : ''}
          </span>
          {kpiData.trend && (
            <Badge variant={kpiData.trend === 'up' ? 'default' : 'destructive'} className="text-xs">
              {kpiData.trend === 'up' ? '↑' : '↓'} {kpiData.change}%
            </Badge>
          )}
        </div>
      </Card>
    );
  };

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Dashboard Widgets</h2>
        <Dialog open={isAddingWidget} onOpenChange={setIsAddingWidget}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Widget
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Widget</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="widget-title">Widget Title</Label>
                <Input
                  id="widget-title"
                  placeholder="Enter widget title"
                  value={widgetTitle}
                  onChange={(e) => setWidgetTitle(e.target.value)}
                />
              </div>

              <div>
                <Label>Widget Template</Label>
                <div className="grid grid-cols-1 gap-2 mt-2">
                  {WIDGET_TEMPLATES.map((template, index) => (
                    <Card
                      key={index}
                      className={`p-3 cursor-pointer border-2 ${
                        selectedTemplate === template ? 'border-primary' : 'border-transparent'
                      }`}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <div className="flex items-center gap-3">
                        {template.type === 'kpi' ? (
                          <TrendingUp className="h-5 w-5 text-primary" />
                        ) : template.chartType === 'line' ? (
                          <LineChartIcon className="h-5 w-5 text-primary" />
                        ) : template.chartType === 'bar' ? (
                          <BarChart3 className="h-5 w-5 text-primary" />
                        ) : (
                          <PieChartIcon className="h-5 w-5 text-primary" />
                        )}
                        <div>
                          <h4 className="font-medium text-sm">{template.title}</h4>
                          <div className="flex gap-1 mt-1">
                            <Badge variant="secondary" className="text-xs">
                              {template.type}
                            </Badge>
                            {template.chartType && (
                              <Badge variant="outline" className="text-xs">
                                {template.chartType}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsAddingWidget(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddWidget}>
                  Add Widget
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {dataSources.length > 0 && (
        <div className="mb-6 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium text-sm mb-2">Connected Data Sources</h4>
          <div className="flex gap-2 flex-wrap">
            {dataSources.map(source => (
              <Badge key={source.id} variant="outline" className="text-xs">
                {source.file_name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {widgets.map(widget => (
          <div key={widget.id} className={`${widget.size === 'large' ? 'lg:col-span-2' : ''}`}>
            {widget.type === 'kpi' ? (
              renderKPIWidget(widget)
            ) : (
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 z-10"
                  onClick={() => removeWidget(widget.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                <DynamicChart
                  config={{
                    id: widget.id,
                    type: widget.chartType!,
                    title: widget.title,
                    data: widget.data,
                    xAxis: 'name',
                    yAxis: 'value',
                    color: widget.color
                  }}
                  data={widget.data}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {widgets.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No widgets added yet.</p>
          <p className="text-sm text-muted-foreground">Click "Add Widget" to get started.</p>
        </div>
      )}
    </Card>
  );
};